import {
  type Numeric,
  clamp,
  createNamespace,
  extend,
  isDef,
} from '@xzx-design/utils'
import type { Ref } from 'vue'
import type { SelectColumn, SelectFieldNames, SelectOption } from './types'

const [name, bem, t] = createNamespace('select')

export { name, bem, t }

export const getFirstEnabledOption = (
  options: SelectOption[]
): SelectOption | undefined =>
  options.find((option) => !option.disabled) || options[0]

export function getColumnsType(
  columns: SelectColumn | SelectColumn[],
  fields: Required<SelectFieldNames>
) {
  const firstColumn = columns[0]
  if (firstColumn) {
    if (Array.isArray(firstColumn)) {
      return 'multiple'
    }
    if (fields.children in firstColumn) {
      return 'cascade'
    }
  }
  return 'default'
}

export function findIndexOfEnabledOption(
  options: SelectOption[],
  index: number
) {
  index = clamp(index, 0, options.length)

  for (let i = index; i < options.length; i++) {
    if (!options[i].disabled) return i
  }
  for (let i = index - 1; i >= 0; i--) {
    if (!options[i].disabled) return i
  }

  return 0
}

export const isOptionExist = (
  options: SelectOption[],
  value: Numeric | undefined,
  fields: Required<SelectFieldNames>
) =>
  value !== undefined &&
  options.some((option) => option[fields.value] === value)

export function findOptionByValue(
  options: SelectOption[],
  value: Numeric,
  fields: Required<SelectFieldNames>
): SelectOption | undefined {
  const index = options.findIndex((option) => option[fields.value] === value)
  const enabledIndex = findIndexOfEnabledOption(options, index)
  return options[enabledIndex]
}

export function formatCascadeColumns(
  columns: SelectColumn | SelectColumn[],
  fields: Required<SelectFieldNames>,
  selectedValues: Ref<Numeric[]>
) {
  const formatted: SelectColumn[] = []

  let cursor: SelectOption | undefined = {
    [fields.children]: columns,
  }
  let columnIndex = 0

  while (cursor && cursor[fields.children]) {
    const options: SelectOption[] = cursor[fields.children]
    const value = selectedValues.value[columnIndex]

    cursor = isDef(value)
      ? findOptionByValue(options, value, fields)
      : undefined

    if (!cursor && options.length) {
      const firstValue = getFirstEnabledOption(options)![fields.value]
      cursor = findOptionByValue(options, firstValue, fields)
    }

    columnIndex++
    formatted.push(options)
  }

  return formatted
}

export function getElementTranslateY(element: Element) {
  const { transform } = window.getComputedStyle(element)
  const translateY = transform.slice(7, -1).split(', ')[5]
  return Number(translateY)
}

export function assignDefaultFields(
  fields: SelectFieldNames | undefined
): Required<SelectFieldNames> {
  return extend(
    {
      text: 'text',
      value: 'value',
      children: 'children',
    },
    fields
  )
}
